/**
 * Content Loader
 * Loads game narratives, dialogues, and text content
 */

import gameContentData from './gameContent.json';
import { STAGE_CONTENT, QUESTIONS, CharacterMessage as GameGuideCharacterMessage, NarrativeSegment } from '@/data/gameGuideContent';
import { PlayerRole } from '../models/Player';
import { StageId } from '../models/GameState';
import { ContainerOption } from '../models/Inventory';

export interface CharacterMessage {
  from: string;
  text: string;
}

export interface Narrative {
  voice: string;
  text: string;
  videoUrl?: string;
  videoOnly?: boolean;
}

export interface EndingNarrative {
  title: string;
  narrative: string;
}

export interface ContainerDescription {
  title: string;
  items: string[];
}

export class ContentLoader {
  private content = gameContentData;

  /**
   * Get character dossier messages for a role
   */
  getCharacterDossier(role: PlayerRole): CharacterMessage[] {
    // Try loading from unified data source first (gameGuideContent.ts)
    const stageContent = STAGE_CONTENT.find(s => s.stage === 0);
    if (stageContent) {
      const upperRole = role.toUpperCase() as keyof typeof stageContent.characterMessages;
      const messages = stageContent.characterMessages[upperRole];
      if (messages && messages.length > 0) {
        // Convert GameGuideCharacterMessage to CharacterMessage format
        return messages.map(msg => ({
          from: this.getMessageSource(msg.icon),
          text: msg.message
        }));
      }
    }

    // Fallback to JSON data
    const upperRole = role.toUpperCase() as keyof typeof this.content.characterDossiers;
    return this.content.characterDossiers[upperRole]?.messages || [];
  }

  /**
   * Convert icon type to message source label
   */
  getMessageSource(icon: string): string {
    const sourceMap: Record<string, string> = {
      'mothership': 'Mothership',
      'home': 'A message from home',
      'gee': 'The Galactic Expert tells you',
      'doc': 'The Doctor tells you',
      'journal': 'Your research journal says',
      'memory': 'Your memory says',
      'conscience': 'Your conscience says',
      'seeing': 'You notice'
    };
    return sourceMap[icon] || icon;
  }

  /**
   * Get stage narrative by key
   */
  getStageNarrative(stage: StageId, key: string): Narrative | null {
    const stageKey = `stage${stage}` as keyof typeof this.content.stageNarratives;
    const stageNarratives = this.content.stageNarratives[stageKey];

    if (!stageNarratives) return null;

    const narrative = stageNarratives[key as keyof typeof stageNarratives];
    return narrative || null;
  }

  /**
   * Get all narratives for a stage
   */
  getAllStageNarratives(stage: StageId): Record<string, Narrative> {
    const stageKey = `stage${stage}` as keyof typeof this.content.stageNarratives;
    return (this.content.stageNarratives[stageKey] as Record<string, Narrative>) || {};
  }

  /**
   * Get ending narrative by type
   */
  getEndingNarrative(endingType: string): EndingNarrative | null {
    const ending = this.content.endings[endingType as keyof typeof this.content.endings];
    return ending || null;
  }

  /**
   * Get container description
   */
  getContainerDescription(container: ContainerOption): ContainerDescription {
    return this.content.containerDescriptions[container];
  }

  /**
   * Get question text (for display purposes)
   * Note: Full question definitions are in Question model
   */
  getQuestionText(_questionId: string): string {
    // Question text is in the Question model
    // This is a helper for any additional narrative context
    return '';
  }

  /**
   * Format narrative text with line breaks
   */
  formatNarrative(text: string): string[] {
    return text.split('\n\n');
  }

  /**
   * Get voice color for character
   */
  getVoiceColor(voice: string): string {
    const colors: Record<string, string> = {
      MOTHERSHIP: '#12356B',
      NARRATOR: '#DD6832',
      GEE: '#367A25',
      DOC: '#12356B'
    };

    return colors[voice] || '#888888';
  }

  /**
   * Get voice icon URL for character
   * Returns the path to the PNG icon file
   */
  getVoiceIcon(voice: string): string {
    const icons: Record<string, string> = {
      MOTHERSHIP: '/icons/MOTHERSHIP.png',
      NARRATOR: '/icons/centauri-logo.png',
      GEE: '/icons/GEE.png',
      DOC: '/icons/doc.png'
    };

    return icons[voice] || '/icons/centauri-logo.png';
  }

  /**
   * Get narrative segments for a stage (from unified data source)
   */
  getNarrativeSegments(stage: StageId, timing?: 'onEntry' | 'afterQuestion' | 'onExit'): NarrativeSegment[] {
    const stageContent = STAGE_CONTENT.find(s => s.stage === stage);
    if (!stageContent || !stageContent.narrativeSequence) {
      return [];
    }

    if (timing) {
      return stageContent.narrativeSequence.filter(seg => seg.displayTiming === timing);
    }

    return stageContent.narrativeSequence;
  }

  /**
   * Convert narrative segment to Narrative format for display
   */
  narrativeSegmentToNarrative(segment: NarrativeSegment): Narrative {
    return {
      voice: segment.speaker,
      text: segment.text
    };
  }

  /**
   * Get character messages for a specific stage and role
   */
  getStageCharacterMessages(
    stage: StageId,
    role: PlayerRole,
    timing?: 'immediate' | 'delayed' | 'afterQuestion'
  ): GameGuideCharacterMessage[] {
    const stageContent = STAGE_CONTENT.find(s => s.stage === stage);
    if (!stageContent) {
      return [];
    }

    const upperRole = role.toUpperCase() as keyof typeof stageContent.characterMessages;
    const messages = stageContent.characterMessages[upperRole] || [];

    if (timing) {
      return messages.filter(msg => msg.timing === timing);
    }

    return messages;
  }

  /**
   * Get questions from unified data source
   */
  getQuestionsForStage(stage: number): typeof QUESTIONS {
    return QUESTIONS.filter(q => q.stage === stage);
  }
}

// Singleton instance
export const contentLoader = new ContentLoader();
